import React, { useCallback, useEffect, useMemo } from 'react';
import MainLayout from 'src/components/layouts/MainLayout';
import styles from 'src/styles/layout.module.css';
import { Breadcrumb, Button, Divider, Image, Row, Space, Spin, Typography, Upload } from 'antd';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { DeleteOutlined, PlaySquareTwoTone } from '@ant-design/icons';
import { AppDispatch } from 'src/index';
import ConfirmButton from 'src/components/atoms/ConfirmButton';
import SwitchSelectField from 'src/components/fields/SwitchSelectField';
import { ContentQuality, VRFormat, VRType } from 'src/features/contents/contents.types';
import { formatDatetime } from 'src/helpers/formatters';
import { FormattedMessage } from 'react-intl';
import SelectEditableField from 'src/components/fields/SelectEditableField';
import TextareaEditableField from 'src/components/fields/TextareaEditableField';
import ReactPlayer from 'react-player';
import InputEditableField from 'src/components/fields/InputEditableField';
import { UploadFile } from 'antd/es/upload/interface';
import { CompanyVideo } from 'src/features/companyVideos/companyVideos.types';
import { deleteCompanyVideo } from 'src/features/companyVideos/companyVideos.state';
import { RoleEnum } from 'src/features/users/users.types';
import ComponentRoleProtected from 'src/components/blocks/ComponentRoleProtected';
import {
  useDeviceGroupVideoById,
  useDeviceGroupVideoImage,
} from 'src/features/deviceGroupVideos/deviceGroupVideos.hooks';
import {
  deleteDeviceGroupVideo,
  fetchDeviceGroupVideo,
  updateDeviceGroupVideo,
} from 'src/features/deviceGroupVideos/deviceGroupVideos.state';
import { DeviceGroupVideo } from 'src/features/deviceGroupVideos/deviceGroupVideos.types';

const DeviceGroupVideoPage: React.FC = () => {
  const { videoId, groupId } = useParams() as { videoId: string; groupId: string };
  const dispatch: AppDispatch = useDispatch();
  const content = useDeviceGroupVideoById(videoId) as DeviceGroupVideo;
  const history = useHistory();
  const { uploadVideoImage } = useDeviceGroupVideoImage({
    videoId,
    groupId,
  });

  const handleUpload = useCallback(
    (file: UploadFile) => {
      uploadVideoImage(file as unknown as File);
    },
    [uploadVideoImage]
  );

  const vrFormatOptions = useMemo(
    () => [
      { label: 'Обычное 2D', value: VRFormat.twoD },
      { label: 'Вертикальное 3D', value: VRFormat.threeDVertical },
      { label: 'Горизонтальное 3D', value: VRFormat.threeDHorizontal },
    ],
    []
  );
  const vrTypeOptions = useMemo(
    () => [
      { label: 'NONE', value: VRType.NONE },
      { label: '180°', value: VRType.VR180 },
      { label: '360°', value: VRType.VR360 },
    ],
    []
  );

  const qualityOptions = useMemo(
    () => [
      { label: 'Нормальное', value: ContentQuality.NORMAL },
      { label: 'Хорошее', value: ContentQuality.GOOD },
      { label: 'Высокое', value: ContentQuality.BEST },
    ],
    []
  );
  const handleDeleteButtonClick = useCallback(async () => {
    const response = await dispatch(deleteDeviceGroupVideo({ groupId, videoId }));
    if (deleteCompanyVideo.fulfilled.match(response)) {
      history.push(`/testing/groups/${groupId}/videos`);
    }
  }, [dispatch, groupId, videoId, history]);
  const handleCardUpdate = useCallback(
    (data: Partial<CompanyVideo>) => {
      dispatch(updateDeviceGroupVideo({ groupId, videoId, data }));
    },
    [dispatch, groupId, videoId]
  );

  return (
    <MainLayout>
      <div className={styles.mainGrid}>
        <div className={styles.breadCrumbs}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to={`/testing/groups/${groupId}/videos`}>
                <Space>
                  <PlaySquareTwoTone />
                  Видео
                </Space>
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`/testing/groups/${groupId}/videos/${videoId}`}>{content.name}</Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className={styles.contentArea}>
          <Space direction={'vertical'} size={16} style={{ width: '100%' }}>
            <InputEditableField level={2} value={content.name} onBlur={e => handleCardUpdate({ name: e })} />
            <Space style={{ width: '100%', marginBottom: 20 }} direction={'vertical'}>
              <ReactPlayer url={content.url} controls={true} />
              <Space direction={'vertical'} style={{ width: '100%' }}>
                <Typography.Text className={styles.title}>Description</Typography.Text>
                <InputEditableField
                  value={content.description as string}
                  onBlur={e => handleCardUpdate({ description: e })}
                />
                <Typography.Text className={styles.title}>URL</Typography.Text>
                <TextareaEditableField value={content.url} onBlur={e => handleCardUpdate({ url: e })} />
                <Typography.Text className={styles.title}>VR Тип</Typography.Text>
                <SelectEditableField
                  value={content.vrType as string}
                  options={vrTypeOptions}
                  onSelect={e => handleCardUpdate({ vrType: e as VRType })}
                />
                <Typography.Text className={styles.title}>VR Формат</Typography.Text>
                <SelectEditableField
                  value={content.vrFormat as string}
                  options={vrFormatOptions}
                  onSelect={e => handleCardUpdate({ vrFormat: e as VRFormat })}
                />
                <Typography.Text className={styles.title}>Качество</Typography.Text>
                <SelectEditableField
                  value={content.quality as string}
                  options={qualityOptions}
                  onSelect={e => handleCardUpdate({ quality: e as ContentQuality })}
                />
                <Typography.Text className={styles.title}>Заблокирован</Typography.Text>
                <SwitchSelectField value={content.disabled} onSelect={e => handleCardUpdate({ disabled: e })} />
                <Typography.Text className={styles.title}>Upscale</Typography.Text>
                <SwitchSelectField value={content.upscale} onSelect={e => handleCardUpdate({ upscale: e })} />
              </Space>
            </Space>
          </Space>
        </div>
        <div className={styles.sidebar}>
          <Space direction={'vertical'}>
            {content.image && <Image src={content.image} width={160} />}
            <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.companyContentManager]}>
              <Upload
                beforeUpload={() => false}
                accept={'image/*'}
                showUploadList={false}
                onChange={e => handleUpload(e.file)}
              >
                <Button type={'primary'}>{content.image ? 'Обновить постер' : 'Загрузить постер'}</Button>
              </Upload>
            </ComponentRoleProtected>
          </Space>
          <Divider />
          <Space direction='vertical' size='small'>
            <Typography.Text className={styles.footerDateStamp}>
              <FormattedMessage id='field.createdAt' />: {formatDatetime(content.createdAt)}
            </Typography.Text>
            <Typography.Text className={styles.footerDateStamp}>
              <FormattedMessage id='field.updatedAt' />: {formatDatetime(content.updatedAt)}
            </Typography.Text>
            <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.companyContentManager]}>
              <ConfirmButton type='link' onClick={handleDeleteButtonClick} danger size='small' style={{ padding: 0 }}>
                <DeleteOutlined /> Удалить
              </ConfirmButton>
            </ComponentRoleProtected>
          </Space>
        </div>
      </div>
    </MainLayout>
  );
};

const DeviceGroupVideoPageBlock: React.FC = () => {
  const { videoId, groupId } = useParams() as { videoId: string; groupId: string };
  const dispatch = useDispatch();
  const video = useDeviceGroupVideoById(videoId);

  useEffect(() => {
    dispatch(fetchDeviceGroupVideo({ groupId, videoId }));
  }, [groupId, dispatch, videoId]);

  if (!video) {
    return (
      <Row align={'middle'} justify={'center'} style={{ minHeight: '100vh' }}>
        <Spin size={'large'} />
      </Row>
    );
  }

  return <DeviceGroupVideoPage />;
};
export default DeviceGroupVideoPageBlock;
